import type {
  AgentId,
  ConversationClientContext,
  ConversationMessageJsonContent,
  DocumentInfo,
} from "@mirage/service-conversation/types";
import { tagged } from "@mirage/service-logging";
import * as rx from "rxjs";
import { createAPIv2GRPCWebPromiseClient } from "../api_v2";
import { convertDocumentInfoToContextEngineDocumentInfo } from "./convertDocumentInfoToContextEngineDocumentInfo";
import type { GetAnswersForQueryResult } from "./gen/context_engine_answers_service_pb";
import { Answers } from "./gen/context_engine_answers_streaming_service_connectweb";
import type {
  QuerySuggestionsResponse,
  SearchParams,
} from "./gen/context_engine_conversations_service_pb";
import {
  ConversationsAssistantResponse,
  ConversationsUserRequest,
  QuerySuggestionsRequest,
} from "./gen/context_engine_conversations_service_pb";
import { Conversations } from "./gen/context_engine_conversations_streaming_service_connectweb";
import { ContextEngineSummarization } from "./gen/context_engine_summarization_service_connectweb";

import type { ConversationsChatMessage } from "./gen/conversations_pb";
import {
  ConversationsUserMessage,
  ResponseFormat,
} from "./gen/conversations_pb";

import type { DocumentId } from "./gen/doc_summarization_pb";
import {
  ClientContext,
  DocumentInfo as ContextEngineDocumentInfo,
} from "./gen/models_pb";
import type { DocSummaryQnaResponse } from "./types";

const logger = tagged("context_Observables");

export function createDocSummarizationObservable(
  resultId: DocumentId,
  fileType: string = "",
  connectorName: string = "",
  docSummarySetting: string = "",
): rx.Observable<DocSummaryQnaResponse> {
  return new rx.Observable<DocSummaryQnaResponse>((subscriber) => {
    const summarizationQnaClient = createAPIv2GRPCWebPromiseClient(
      ContextEngineSummarization,
    );
    if (resultId) {
      (async () => {
        try {
          for await (const res of summarizationQnaClient.summarizeDoc({
            id: resultId,
            fileType: fileType,
            connectorName: connectorName,
            setting: docSummarySetting,
          })) {
            if (res?.summary) {
              subscriber.next({
                answer: res.summary,
                requestId: res.requestId,
              });
            }
          }
          subscriber.complete();
        } catch (error) {
          logger.error("createDocSummarizationObservable error", error);
          subscriber.error(error);
        }
      })();
    }
  });
}

export function createDocAnswerObservable(
  resultId: DocumentId,
  question: string,
  fileType: string = "",
  connectorName: string = "",
  docQnaSetting: string = "",
): rx.Observable<DocSummaryQnaResponse> {
  return new rx.Observable<DocSummaryQnaResponse>((subscriber) => {
    const summarizationQnaClient = createAPIv2GRPCWebPromiseClient(
      ContextEngineSummarization,
    );
    if (resultId && question) {
      (async () => {
        try {
          for await (const res of summarizationQnaClient.getAnswerFromDoc({
            id: resultId,
            question: question,
            fileType: fileType,
            connectorName: connectorName,
            setting: docQnaSetting,
          })) {
            if (res?.answer) {
              subscriber.next({
                answer: res.answer,
                requestId: res.requestId,
              });
            }
          }
          subscriber.complete();
        } catch (error) {
          logger.error("createDocAnswerObservable error", error);
          subscriber.error(error);
        }
      })();
    }
  });
}

export function createGenAnswersForQueryObservable(
  userQuery: string,
  experimentSetting: string = "",
  source?: string,
): rx.Observable<GetAnswersForQueryResult> {
  // If no userQuery, just return an empty Observable that completes immediately
  if (!userQuery) {
    return rx.EMPTY;
  }

  const answersGrpcProxyClient = createAPIv2GRPCWebPromiseClient(Answers);

  async function* generator() {
    try {
      const req = answersGrpcProxyClient.genAnswersForQuery({
        userQuery,
        experimentSetting,
        source: source ?? "dash",
      });
      for await (const res of req) {
        yield res;
      }
    } catch (error) {
      // Let RxJS observe the error
      logger.error("createGenAnswersForQueryObservable error", error);
      throw error;
    }
  }

  return rx.from(generator());
}

/**
 * Extends ConversationsChatMessage type to include json. This is temporary transport
 * data that gets set in convertToConversationMessage and then discarded; we can use
 * a private property so as to not pollute the message type.
 */
export interface ConversationsChatMessageWithJson
  extends ConversationsChatMessage {
  _jsonContent?: ConversationMessageJsonContent;
}

// Override the response type to include messages with JSON content.
export class ConversationsAssistantResponseWithJsonMessages extends ConversationsAssistantResponse {
  override messages: ConversationsChatMessageWithJson[] = [];
  // This is the unprocessed response from the server, used for logging only
  unprocessedResponse: ConversationsAssistantResponse | undefined;
}

export function createConversationResponseObservable(
  ...args: Parameters<typeof getConversationResponseRequest>
): rx.Observable<ConversationsAssistantResponseWithJsonMessages> {
  const conversationsClient = createAPIv2GRPCWebPromiseClient(Conversations);

  async function* generator() {
    try {
      const request = getConversationResponseRequest(...args);
      for await (const res of conversationsClient.getConversationResponse(
        request,
      )) {
        const processedMessages = processMessages(res.messages);
        const processedResponse = {
          ...res,
          messages: processedMessages,
          unprocessedResponse: res,
        } as ConversationsAssistantResponseWithJsonMessages;
        yield processedResponse;
      }
    } catch (error) {
      logger.error("createConversationResponseObservable error", error);
      throw error;
    }
  }

  // throttle streaming responses to avoid overwhelming the callers
  return rx.from(generator()).pipe(
    rx.throttleTime(10, undefined /* scheduler */, {
      leading: true,
      trailing: true,
    }),
  );
}

export function getConversationResponseRequest(
  userMessage: string,
  conversationId: string | undefined,
  history?: ConversationsChatMessage[],
  experimentSetting?: string,
  source?: string,
  modelChoice?: string,
  agentPayload?: string,
  attachments?: DocumentInfo[],
  agentIds?: AgentId[],
  rephrased?: string,
  clientContext?: ConversationClientContext,
  searchParams?: SearchParams,
  skipPersistence?: boolean,
  fssSearchScope?: string,
  llmIgnoreUserMessage?: boolean,
  stackId?: string,
): ConversationsUserRequest {
  const userMessageObj = new ConversationsUserMessage({
    text: userMessage,
    agentPayload: agentPayload ?? "{}",
    attachments:
      attachments?.map(convertDocumentInfoToContextEngineDocumentInfo) ?? [],
    agentIds,
    rephrased,
    fssSearchScope,
    llmIgnore: llmIgnoreUserMessage,
  });
  let clientContextPayload;
  if (clientContext) {
    clientContextPayload = new ClientContext({
      clientScenario: clientContext.clientScenario,
      clientScenarioId: clientContext.clientScenarioId,
    });
  }
  return new ConversationsUserRequest({
    conversationId: conversationId,
    history: history,
    userMessage: userMessageObj,
    experimentSetting: experimentSetting,
    source: source,
    modelChoice: modelChoice,
    clientContext: clientContextPayload,
    searchParams: searchParams,
    skipPersistence: skipPersistence,
    stackId: stackId,
  });
}

/**
 * Process messages to detect and parse JSON content. Returns new array
 * of messages with JSON format and content where applicable.
 */
export function processMessages(
  messages: ConversationsChatMessage[],
): ConversationsChatMessageWithJson[] {
  return messages.map((message) => {
    if (
      message.messageType.case === "assistantMessage" &&
      message.messageType.value.format === ResponseFormat.JSON
    ) {
      const text = message.messageType.value.text;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object") {
          return {
            ...message,
            messageType: {
              ...message.messageType,
              value: {
                ...message.messageType.value,
                text: parsed.draft_modifications || "",
              },
            },
            _jsonContent: {
              content: parsed.content,
              draftModifications: parsed.draft_modifications,
              calendarEvents: parsed.items,
            },
          } as ConversationsChatMessageWithJson;
        }
      } catch (error) {
        logger.error(
          "Message text is of type JSON format but is not valid JSON",
        );
      }
    }
    return message;
  });
}

export function createQuerySuggestionsObservable(
  userQuery?: string,
  documentIds?: string[],
  experimentSetting?: string,
  source?: string,
  clientContext?: ClientContext,
  _debug?: boolean,
  stackId?: string,
  maxSuggestions?: number,
  retriever?: string,
  fssSearchScope?: string,
  documentInfos?: ContextEngineDocumentInfo[],
): rx.Observable<QuerySuggestionsResponse> {
  const conversationsClient = createAPIv2GRPCWebPromiseClient(Conversations);

  async function* generator() {
    try {
      const request = new QuerySuggestionsRequest({
        clientContext: clientContext,
        debug: true,
        userQuery: userQuery,
        documentIds: documentIds,
        experimentSetting: experimentSetting,
        source: source,
        stackId: stackId,
        maxSuggestions: maxSuggestions,
        retriever: retriever,
        fssSearchScope: fssSearchScope,
        documentInfos: documentInfos,
      });

      for await (const res of conversationsClient.genQuerySuggestions(
        request,
      )) {
        if (res?.querySuggestions?.length > 0) {
          yield res;
        }
      }
    } catch (error) {
      logger.error("createQuerySuggestionsObservable error", error);
      throw error;
    }
  }

  return rx.from(generator());
}
